import React, { useMemo, useState } from "react";
import { ThumbsDown } from "lucide-react";
import { useGetFeedbackQuery } from "../../../../store/apis/helpCenter.api";
import {
  Button,
  Pagination2,
} from "../../../common";
import TicketDetailModal from "../modals/TicketDetailModal";
import { ArticleTableSkeleton } from "../Skeleton";
import { getFeedbackDisplay } from "../../../../utils/helpCenter";
import { formatDDMMYYYY } from "../../../../utils/timeUtils";

/** Figma Not-helpful Feedback panel (dc.html:1925) fed by real feedback tickets. */
const ManageTickets: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const { data, isLoading, isFetching, isError, refetch } = useGetFeedbackQuery({
    page,
    limit,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);

  const tickets = (data as any)?.data || [];
  const total = (data as any)?.total || 0;

  const handleView = (ticket: any) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const rows = useMemo(
    () =>
      tickets.map((t: any) => {
        const display = getFeedbackDisplay(t.description || "");
        return { ticket: t, ...display };
      }),
    [tickets]
  );

  const loading = isLoading || isFetching;

  if (isError) {
    return (
      <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-[18px] shadow-[var(--shadow-sm)] text-center px-6 py-14">
        <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load feedback</h3>
        <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
          Something went wrong while fetching feedback. Please try again.
        </p>
        <Button appearance="primary" size="small" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-[18px] shadow-[var(--shadow-sm)] p-0 overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap px-[22px] py-[18px] border-b border-[var(--color-border-tertiary)]">
        <div>
          <h3 className="text-heading-sm text-[var(--color-text-primary)]">
            Not-helpful Feedback
          </h3>
          <p className="mt-[3px] text-body-sm text-[var(--color-text-tertiary)]">
            Reasons employees gave when an article did not help
          </p>
        </div>
        {!loading && (
          <span className="text-caption font-bold text-[var(--color-error-dark)] bg-[var(--color-error-light)] px-[11px] py-1 rounded-full flex-none">
            {total} responses
          </span>
        )}
      </div>

      {loading ? (
        <ArticleTableSkeleton rows={5} />
      ) : rows.length > 0 ? (
        <div>
          {rows.map(({ ticket, reasons, note }: any) => (
            <button
              key={ticket._id || ticket.id}
              type="button"
              onClick={() => handleView(ticket)}
              className="w-full flex items-start gap-3 p-[14px_22px] border-t border-[var(--color-border-tertiary)] cursor-pointer transition-colors hover:bg-[var(--color-bg-hover)] text-left first:border-t-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
            >
              <span className="w-[34px] h-[34px] rounded-button flex-none grid place-items-center bg-[var(--color-error-light)]">
                <ThumbsDown className="w-4 h-4 text-[var(--color-error-dark)]" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center justify-between gap-2.5 flex-wrap">
                  <span className="text-label font-bold text-[var(--color-text-primary)] truncate">
                    {ticket.articleTitle || "General feedback"}
                  </span>
                  <span className="text-body-xs font-semibold text-[var(--color-text-tertiary)] flex-none">
                    {ticket.email || "Anonymous"} · {formatDDMMYYYY(ticket.createdAt) ?? "Recently"}
                  </span>
                </span>
                {reasons.length > 0 && (
                  <span className="mt-[5px] flex flex-wrap gap-1.5">
                    {reasons.map((r: string) => (
                      <span
                        key={r}
                        className="inline-flex text-caption font-bold text-[var(--color-warning-dark)] bg-[var(--color-warning-light)] px-[9px] py-[3px] rounded-full"
                      >
                        {r}
                      </span>
                    ))}
                  </span>
                )}
                {note && (
                  <span className="block mt-[7px] text-body-sm text-[var(--color-text-strong)] leading-[1.5] line-clamp-2">
                    {note}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-[22px] py-10 text-center border-t border-[var(--color-border-tertiary)]">
          <div className="text-body-md font-bold text-[var(--color-text-primary)]">
            No feedback yet
          </div>
          <div className="text-body-sm text-[var(--color-text-tertiary)] mt-1">
            Employee feedback will appear here once articles are rated.
          </div>
        </div>
      )}

      {total > limit && (
        <div className="px-5 py-3 border-t border-[var(--color-border-tertiary)]">
          <Pagination2
            total={total}
            page={page}
            pageSize={limit}
            pageSizeOptions={[5, 10, 20, 50]}
            onPageChange={setPage}
            onPageSizeChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
            autoHide
          />
        </div>
      )}

      {selectedTicket && (
        <TicketDetailModal
          isOpen={isModalOpen}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default ManageTickets;
